import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { supabase } from '../services/supabase';
import { CustomerAddress, PickupPoint } from '../types';
import {
    calculateDeliveryPriceFromDB,
    calculateDeliveryPriceSync,
    subscribeToDeliveryConfig,
} from '../utils/deliveryConfig';
import toast from 'react-hot-toast';
import {
    TruckIcon,
    BuildingStorefrontIcon,
    ArrowLeftIcon,
    MapPinIcon,
    ClockIcon,
    PhoneIcon,
    PlusIcon,
} from '@heroicons/react/24/outline';
import AddAddressDialog from '../components/AddAddressDialog';

type DeliveryType = 'courier' | 'pickup';

export const DeliverySelection: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { cartItems, totalAmount } = useCart();

    const fromCheckout = location.state?.from === 'checkout';
    const totalQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);

    const [activeTab, setActiveTab] = useState<DeliveryType>('courier');
    const [addresses, setAddresses] = useState<CustomerAddress[]>([]);
    const [selectedAddress, setSelectedAddress] = useState<CustomerAddress | null>(null);
    const [pickupPoints, setPickupPoints] = useState<PickupPoint[]>([]);
    const [selectedPickupPoint, setSelectedPickupPoint] = useState<PickupPoint | null>(null);
    const [loadingData, setLoadingData] = useState(true);
    const [showAddressDialog, setShowAddressDialog] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deliveryPrice, setDeliveryPrice] = useState(0);
    const [loadingPrice, setLoadingPrice] = useState(true);
    const [userId, setUserId] = useState<string | null>(null);
    const [userCountryCode, setUserCountryCode] = useState<string>('TJ');

    // Загружаем сессию при монтировании
    useEffect(() => {
        const loadSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user) {
                setUserId(session.user.id);
                setUserCountryCode(session.user.user_metadata?.country_code || 'TJ');
            }
            setLoadingData(false);
        };
        loadSession();
    }, []);

    const loadAllData = useCallback(async () => {
        if (!userId) return;

        setLoadingData(true);
        try {
            const [addressesRes, pickupRes, settingsRes] = await Promise.all([
                supabase
                    .from('customer_addresses')
                    .select('*')
                    .eq('customer_id', userId)
                    .order('is_default', { ascending: false }),
                supabase
                    .from('pickup_points')
                    .select('*')
                    .eq('is_active', true)
                    .order('name'),
                supabase
                    .from('customer_delivery_settings')
                    .select('*')
                    .eq('customer_id', userId)
                    .maybeSingle(),
            ]);

            if (addressesRes.error) throw addressesRes.error;
            if (pickupRes.error) throw pickupRes.error;

            const addressList: CustomerAddress[] = addressesRes.data || [];
            const pointList: PickupPoint[] = pickupRes.data || [];
            const settings = settingsRes.data;

            setAddresses(addressList);
            setPickupPoints(pointList);

            if (settings) {
                const method: DeliveryType = settings.delivery_type === 'pickup' ? 'pickup' : 'courier';
                setActiveTab(method);
                if (method === 'courier' && settings.address_id) {
                    const addr = addressList.find(a => a.id === settings.address_id);
                    if (addr) setSelectedAddress(addr);
                } else if (method === 'pickup' && settings.pickup_point_id) {
                    const point = pointList.find(p => p.id === settings.pickup_point_id);
                    if (point) setSelectedPickupPoint(point);
                }
            } else {
                const defaultAddr = addressList.find(a => a.is_default) || addressList[0] || null;
                if (defaultAddr) setSelectedAddress(defaultAddr);
            }
        } catch (err) {
            console.error('Error loading delivery data:', err);
            toast.error('Ошибка загрузки данных доставки');
        } finally {
            setLoadingData(false);
        }
    }, [userId]);

    useEffect(() => {
        if (userId) {
            loadAllData();
        }
    }, [userId, loadAllData]);

    useEffect(() => {
        if (activeTab === 'pickup') {
            setDeliveryPrice(0);
            setLoadingPrice(false);
            return;
        }
        setLoadingPrice(true);
        calculateDeliveryPriceFromDB(totalQuantity, userCountryCode).then(price => {
            setDeliveryPrice(price);
            setLoadingPrice(false);
        });
    }, [activeTab, totalQuantity, userCountryCode]);

    useEffect(() => {
        const unsubscribe = subscribeToDeliveryConfig((_, countryCode) => {
            if (countryCode === userCountryCode && activeTab !== 'pickup') {
                setDeliveryPrice(calculateDeliveryPriceSync(totalQuantity, userCountryCode));
            }
        });
        return () => unsubscribe();
    }, [activeTab, totalQuantity, userCountryCode]);

    const saveDeliverySettings = async () => {
        if (!userId) {
            toast.error('Войдите в аккаунт');
            navigate('/login');
            return;
        }

        if (activeTab === 'courier' && !selectedAddress) {
            toast.error('Выберите адрес доставки');
            return;
        }
        if (activeTab === 'pickup' && !selectedPickupPoint) {
            toast.error('Выберите пункт выдачи');
            return;
        }

        setSaving(true);
        try {
            const { data, error } = await supabase.rpc('upsert_delivery_settings', {
                p_delivery_type: activeTab,
                p_address_id: activeTab === 'courier' ? (selectedAddress?.id || null) : null,
                p_pickup_point_id: activeTab === 'pickup' ? (selectedPickupPoint?.id || null) : null,
            });
            if (error) throw error;
            if (data && data.success === false) throw new Error(data.message);

            toast.success('Способ доставки сохранен');
            navigate(fromCheckout ? '/checkout' : '/profile', {
                state: fromCheckout ? { from: 'delivery-selection' } : undefined,
            });
        } catch (error: any) {
            console.error('Error saving delivery settings:', error);
            toast.error(error.message || 'Ошибка сохранения способа доставки');
        } finally {
            setSaving(false);
        }
    };

    const handleAddressAdded = (address: CustomerAddress) => {
        setAddresses(prev => [address, ...prev]);
        setSelectedAddress(address);
        setShowAddressDialog(false);
    };

    const finalTotal = totalAmount + deliveryPrice;
    const canSave = (activeTab === 'courier' && !!selectedAddress) || (activeTab === 'pickup' && !!selectedPickupPoint);

    if (loadingData || loadingPrice) {
        return (
            <div className="loader-container">
                <div className="spinner" />
                <p>Загрузка...</p>
            </div>
        );
    }

    if (!userId) {
        return (
            <div className="empty-state">
                <div className="empty-state-icon">👤</div>
                <h3>Войдите в аккаунт</h3>
                <p>Чтобы выбрать способ доставки, войдите в аккаунт</p>
                <button onClick={() => navigate('/login')} className="auth-button">
                    Войти
                </button>
            </div>
        );
    }

    return (
        <div className="delivery-selection">
            <div className="delivery-header">
                <button onClick={() => navigate(-1)} className="back-button">
                    <ArrowLeftIcon style={{ width: 20, height: 20 }} /> Назад
                </button>
                <h1>Способ доставки</h1>
            </div>

            <div className="delivery-tabs">
                <button
                    className={`tab ${activeTab === 'courier' ? 'active' : ''}`}
                    onClick={() => setActiveTab('courier')}
                >
                    <TruckIcon style={{ width: 20, height: 20 }} /> Курьер
                </button>
                <button
                    className={`tab ${activeTab === 'pickup' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pickup')}
                >
                    <BuildingStorefrontIcon style={{ width: 20, height: 20 }} /> Пункт выдачи
                </button>
            </div>

            <div className="delivery-content">
                {activeTab === 'courier' && (
                    <div className="courier-section">
                        <div className="section-header">
                            <h2>Адрес доставки</h2>
                            <button
                                className="add-address-btn"
                                onClick={() => setShowAddressDialog(true)}
                            >
                                <PlusIcon style={{ width: 16, height: 16 }} /> Добавить адрес
                            </button>
                        </div>
                        {addresses.length === 0 ? (
                            <div className="empty-addresses">
                                <MapPinIcon style={{ width: 40, height: 40 }} />
                                <p>У вас нет сохранённых адресов</p>
                                <button onClick={() => setShowAddressDialog(true)}>
                                    Добавить адрес
                                </button>
                            </div>
                        ) : (
                            <div className="addresses-list">
                                {addresses.map(addr => (
                                    <div
                                        key={addr.id}
                                        className={`address-item ${selectedAddress?.id === addr.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedAddress(addr)}
                                    >
                                        <div className="address-radio">
                                            <div className={`radio ${selectedAddress?.id === addr.id ? 'selected' : ''}`} />
                                        </div>
                                        <div className="address-info">
                                            <div className="address-full">
                                                {addr.city}, {addr.street}, {addr.house}
                                                {addr.apartment && `, кв. ${addr.apartment}`}
                                            </div>
                                            {addr.is_default && <span className="default-badge">Основной</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="delivery-info">
                            <div className="info-row">
                                <TruckIcon style={{ width: 18, height: 18 }} />
                                <span>
                                    Стоимость доставки:{' '}
                                    {deliveryPrice === 0
                                        ? 'Бесплатно'
                                        : `${deliveryPrice.toLocaleString()} TJS`}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'pickup' && (
                    <div className="pickup-section">
                        <h2>Выберите пункт выдачи</h2>
                        {pickupPoints.length === 0 ? (
                            <div className="empty-state">
                                <BuildingStorefrontIcon style={{ width: 48, height: 48 }} />
                                <p>Пункты выдачи не найдены</p>
                            </div>
                        ) : (
                            <div className="pickup-points-list">
                                {pickupPoints.map(point => (
                                    <div
                                        key={point.id}
                                        className={`pickup-point-card ${selectedPickupPoint?.id === point.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedPickupPoint(point)}
                                    >
                                        <div className="pickup-point-radio">
                                            <div className={`radio ${selectedPickupPoint?.id === point.id ? 'selected' : ''}`} />
                                        </div>
                                        <div className="pickup-point-info">
                                            <h3>{point.name}</h3>
                                            <div className="pickup-point-address">
                                                <MapPinIcon style={{ width: 14, height: 14 }} />
                                                <span>{point.address}</span>
                                            </div>
                                            {point.working_hours && (
                                                <div className="pickup-point-hours">
                                                    <ClockIcon style={{ width: 14, height: 14 }} />
                                                    <span>{point.working_hours}</span>
                                                </div>
                                            )}
                                            {point.phone && (
                                                <div className="pickup-point-phone">
                                                    <PhoneIcon style={{ width: 14, height: 14 }} />
                                                    <span>{point.phone}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="delivery-info pickup-info">
                            <div className="info-row">
                                <BuildingStorefrontIcon style={{ width: 18, height: 18 }} />
                                <span>Самовывоз — Бесплатно</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {cartItems.length > 0 && (
                <div className="delivery-summary">
                    <div className="summary-row">
                        <span>Товары ({totalQuantity} шт.):</span>
                        <span>{totalAmount.toLocaleString()} TJS</span>
                    </div>
                    <div className="summary-row">
                        <span>Доставка:</span>
                        <span>
                            {deliveryPrice === 0 ? 'Бесплатно' : `${deliveryPrice.toLocaleString()} TJS`}
                        </span>
                    </div>
                    <div className="summary-total">
                        <span>Итого:</span>
                        <span>{finalTotal.toLocaleString()} TJS</span>
                    </div>
                </div>
            )}

            <button
                className="continue-btn"
                onClick={saveDeliverySettings}
                disabled={!canSave || saving}
            >
                {saving ? 'Сохранение...' : 'Сохранить настройки'}
            </button>

            {showAddressDialog && userId && (
                <AddAddressDialog
                    customerId={userId}
                    onClose={() => setShowAddressDialog(false)}
                    onAddressAdded={handleAddressAdded}
                />
            )}
        </div>
    );
};